
import { MediaItem } from "@/interfaces/media-item";
import { PlayerActions } from "@/store/store.actions";
import { PlayerGetters } from "@/store/store.getters";
import Component from "vue-class-component";
import { Prop, Ref, Watch } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";
import { urlParamService } from "../../services/urlParam.service";
import BaseMedia from "./BaseMedia.vue";

@Component({})
export default class VideoMedia extends BaseMedia implements MediaItem {
  url = "";
  posterUrl = "";
  loaded = false;
  isPlaying = false;
  internallyMuted = false;
  remountKey = 0;
  private startTimer: ReturnType<typeof setTimeout> | null = null;
  @Ref() mediaItem!: HTMLVideoElement;
  @Prop({ required: false, default: 0 }) videoBorderSize!: number;

  @Getter(PlayerGetters.muted) muted!: boolean;
  @Getter(PlayerGetters.playAudioPlaylist) playAudioPlaylist!: boolean;

  @Action(PlayerActions.triggerPlaylistEntry) triggerPlaylistEntry!: ({
    prev,
    triggered,
    prevIndex,
    index,
  }: {
    prev: boolean;
    triggered: boolean;
    index?: number;
    prevIndex?: number;
  }) => Promise<void>;

  mounted() {
    this.url = this.mediaData.contentItem.url;
    this.posterUrl = this.mediaData.contentItem.thumbnail_url || "";
    if (this.posterUrl && urlParamService.useCdn()) {
      this.posterUrl = `https://cdn.contentconvert.alphaport.at/api/v1/image/h_${
        this.$vuetify.breakpoint.height
      },q_80,f_cover/resized.webp?url=${encodeURIComponent(this.posterUrl)}`;
    }
    this.mediaItem?.pause();
    this.loaded = true;
  }

  async startMedia() {
    // Cancel any previous pending start to avoid overlapping loads on fast switches
    this.cancelStartTimer();

    // Brief debounce: if stopMedia() is called before this resolves, the load is skipped entirely
    const cancelled = await new Promise<boolean>((resolve) => {
      this.startTimer = setTimeout(() => {
        this.startTimer = null;
        resolve(false);
      }, 150);
    });
    if (cancelled || !this.active) return;

    await this.$nextTick();
    this.mediaItem.classList.add("active");
    this.internallyMuted = this.muted || this.playAudioPlaylist;

    if (!this.isPlaying) {
      try {
        this.mediaItem.load();
        this.mediaItem.muted = this.internallyMuted;
        await this.mediaItem.play();
      } catch (err) {
        // when web player is started with mute-audio=false play will fail with volume when no user interaction happened
        if (!this.internallyMuted && !this.paused) {
          this.internallyMuted = true;
          this.mediaItem.muted = true;
          await this.mediaItem.play().catch((err) => {
            console.error("Error occured!", err);
          });
        }
      }
    }
  }

  async stopMedia() {
    // Cancel any debounced start so a pending load never begins after we stop
    this.cancelStartTimer();

    console.log("VideoMedia::stopMedia()");
    await this.$nextTick();
    if (!this.alwaysVisible) {
      this.mediaItem?.classList.remove("active");
    }
    this.mediaItem.pause();
    if (urlParamService.layout === "iptv") {
      // HINT: if you call this on ocilion STB, the video is started again -> thats why we use load()
      this.mediaItem.load();
    }
  }

  private cancelStartTimer() {
    if (this.startTimer !== null) {
      clearTimeout(this.startTimer);
      this.startTimer = null;
    }
  }

  @Watch("paused")
  async pausedWatch() {
    if (this.isError || !this.loaded || !this.active) {
      return;
    }
    if (this.paused && !this.mediaItem.paused && this.isPlaying) {
      this.mediaItem.pause();
    } else if (this.mediaItem.paused && !this.isPlaying) {
      this.mediaItem.play();
    }
  }

  @Watch("muted")
  async mutedWatch(muted: boolean) {
    if (this.isError) return;
    this.internallyMuted = muted || this.playAudioPlaylist;
    if (this.mediaItem) {
      this.mediaItem.muted = this.internallyMuted;
    }
  }

  async videoEndedEvent() {
    this.isPlaying = false;
    console.log("VideoMedia::videoEndedEvent() - triggerPlaylistEntry", new Date().getTime());
    await this.triggerPlaylistEntry({ prev: false, triggered: false });
  }

  handleVideoError() {
    const error = this.mediaItem?.error;
    console.error("VideoMedia::handleVideoError()", error?.code, error?.message);

    if (!error || !this.active) return;

    // MEDIA_ERR_ABORTED = 1
    // MEDIA_ERR_NETWORK = 2 (network error, HTTP errors, etc.)
    // MEDIA_ERR_DECODE = 3
    // MEDIA_ERR_SRC_NOT_SUPPORTED = 4

    if (error.code === 2 || error.code === 3) {
      // Network or decode error - try to recover
      console.log("Attempting to recover from network/decode error");
      this.attemptRecovery();
    }
  }

  handleVideoStalled() {
    if (!this.active) return;
    console.warn("VideoMedia::handleVideoStalled() - video playback stalled");
    this.attemptRecovery();
  }

  private async attemptRecovery() {
    // Immediate recovery: force a DOM remount and reload the video element
    try {
      this.remountKey++;
      await this.$nextTick();

      // try to reinitialize and play the new element
      this.mediaItem.load();
      this.mediaItem.muted = this.internallyMuted;
      await this.mediaItem.play();
      console.log("VideoMedia: Recovery via immediate remount succeeded");
    } catch (err) {
      console.error("VideoMedia: Immediate remount recovery failed", err);
      // fallback: consider the entry finished and move on
      this.videoEndedEvent();
    }
  }
}
